import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Rocket, ArrowRight, Radar, Target, Compass, ArrowUpRight, TrendingUp, Quote } from "lucide-react";
import { Button } from "@/components/ui/button";

const FIELDS_PREVIEW = [
  "Transparenz schaffen",
  "Finanzstrategie festlegen",
  "Finanzen planen",
  "Finanzierung optimieren",
  "Reporting standardisieren",
  "Profitabilität steigern",
  "Nachfolgefähigkeit sicherstellen",
];

const CASE_STATS = [
  { label: "Monatsabschluss", before: "6 Wochen zu spät", after: "bis zum 8." },
  { label: "EBITDA-Marge", before: "unbekannt", after: "+2 Punkte" },
  { label: "Forderungslaufzeit", before: "55 Tage", after: "38 Tage" },
  { label: "Bankenrating", before: "eine Hausbank", after: "2 Stufen besser" },
];

export const Landing = ({ onStart }) => {
  return (
    <div data-testid="landing-page" className="relative">
      {/* HERO */}
      <section className="relative overflow-hidden min-h-[88vh] flex items-center">
        <img
          src="/hero-rocket-new.jpg"
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover opacity-60 pointer-events-none"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/85 to-black/30 pointer-events-none" aria-hidden />
        <div className="absolute inset-0 grid-lines opacity-30 pointer-events-none" aria-hidden />

        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24 w-full">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <div className="inline-flex items-center gap-2 border border-brand-orange/40 bg-brand-orange/10 px-3 py-1.5 mb-8">
              <Compass className="h-3.5 w-3.5 text-brand-orange" />
              <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">
                Mission Control · CFO as-a-Service
              </span>
            </div>

            <h1 className="font-display uppercase text-4xl sm:text-6xl leading-[1.05] tracking-tight text-white">
              Wie tragfähig sind Ihre Finanzen?
              <br />
              <span className="text-brand-orange">In 5 Minuten wissen Sie es.</span>
            </h1>

            <p className="mt-8 text-base sm:text-lg text-white/75 leading-relaxed max-w-2xl">
              Wer seine Zahlen nicht kennt, verschenkt Wert. Der Tragfähigkeits-Check macht in 7
              Feldern sichtbar, wo Sie im Blindflug unterwegs sind, wo Sie den Kurs halten — und mit
              welchen Hebeln Ihr Unternehmen wertvoller wird.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button
                data-testid="landing-start-btn"
                onClick={onStart}
                className="group h-14 px-8 rounded-none bg-brand-orange text-black hover:bg-white font-bold uppercase tracking-wider text-sm transition-colors duration-300"
              >
                <Rocket className="mr-2 h-5 w-5" />
                Lagecheck starten
                <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </Button>
              <div className="flex items-center gap-3 text-white/50 text-sm px-2">
                7 Felder · ca. 5 Minuten · ohne Anmeldung
              </div>
            </div>

            <div className="mt-12 flex items-center gap-8 text-white/70">
              {["Mehr Cash", "Mehr Flow", "Mehr Wert"].map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <ArrowUpRight className="h-4 w-4 text-brand-orange" />
                  <span className="font-display text-sm tracking-tight">{t}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* EXPLAINER VIDEO */}
      <section
        className="relative max-w-7xl mx-auto px-6 sm:px-10 py-16 border-t border-white/10"
        data-testid="landing-video-section"
      >
        <span className="block text-center text-xs tracking-[0.2em] uppercase text-brand-orange">
          So funktioniert der Check
        </span>
        <div className="mt-6 max-w-[960px] mx-auto">
          <video
            className="w-full aspect-video rounded-xl border border-white/15 bg-black"
            controls
            preload="metadata"
            playsInline
            poster={`${process.env.PUBLIC_URL}/media/tragfaehigkeits-check-poster.jpg`}
            aria-label="Video-Anleitung: So funktioniert der Tragfähigkeits-Check"
            data-testid="landing-explainer-video"
          >
            <source src={`${process.env.PUBLIC_URL}/media/tragfaehigkeits-check-anleitung.mp4`} type="video/mp4" />
            Ihr Browser unterstützt die Video-Wiedergabe nicht.
          </video>
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20 border-t border-white/10">
        <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">
          Der Kontrollraum
        </span>
        <h2 className="mt-4 font-display uppercase text-2xl sm:text-3xl tracking-tight text-white max-w-2xl">
          Der Unternehmenswert ist die einzige Kennzahl, die zeigt: Was war. Was ist. Was möglich ist.
        </h2>
        <div className="mt-12 grid md:grid-cols-3 gap-6">
          {[
            { icon: Radar, title: "Ihre Lage in 7 Feldern", text: "Vom Blindflug bis zum Instrumentenflug: Sie sehen pro Feld, wo Sie stehen — und wo Wert verloren geht." },
            { icon: Target, title: "Der Benchmark", text: "Wo steht der Mittelstand typischerweise — und wie weit sind Sie vom wertstarken Kurs entfernt?" },
            { icon: Compass, title: "Ihr nächster Schritt", text: "Für jedes Feld: was Best Practice ist und der konkrete nächste Schritt, um eine Stufe besser zu steuern." },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className="bg-card border border-white/10 p-8 hover:border-brand-orange/50 transition-colors duration-300"
            >
              <c.icon className="h-8 w-8 text-brand-orange" />
              <h3 className="mt-6 font-display text-base uppercase tracking-tight text-white">
                {c.title}
              </h3>
              <p className="mt-3 text-sm text-white/60 leading-relaxed">{c.text}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CASE STUDY */}
      <section className="relative border-y border-white/10 bg-[#0a0a0a]">
        <div className="absolute inset-0 grid-lines opacity-20 pointer-events-none" aria-hidden />
        <div className="relative max-w-7xl mx-auto px-6 sm:px-10 py-20">
          <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">Fallstudie</span>
          <h2 className="mt-4 font-display uppercase text-2xl sm:text-4xl tracking-tight text-white leading-tight max-w-3xl">
            Vom Blindflug zum Instrumentenflug
          </h2>
          <p className="mt-3 text-sm text-white/50">
            Maschinenbauer · 8 Mio. € Umsatz · 45 Mitarbeitende · Zeitraum: 3 Jahre
          </p>

          <div className="mt-12 grid lg:grid-cols-3 gap-8 items-start">
            {/* Story */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex gap-4">
                <span className="mt-1 h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: "#ef4444" }} />
                <div>
                  <span className="text-xs uppercase tracking-[0.15em]" style={{ color: "#ef4444" }}>
                    Ausgangslage · Blindflug
                  </span>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">
                    Die Monatszahlen kamen sechs Wochen zu spät. Eine Hausbank, private Sicherheiten,
                    Preise ohne Nachkalkulation. Der Inhaber steuerte aus dem Bauch – und wusste
                    nicht, was sein Unternehmen wert ist.
                  </p>
                </div>
              </div>
              <div className="flex gap-4">
                <span className="mt-1 h-3 w-3 rounded-full shrink-0" style={{ backgroundColor: "#22c55e" }} />
                <div>
                  <span className="text-xs uppercase tracking-[0.15em]" style={{ color: "#22c55e" }}>
                    Heute · Instrumentenflug
                  </span>
                  <p className="mt-2 text-sm text-white/70 leading-relaxed">
                    Ein fester Monatsabschluss bis zum 8., ein rollierender Forecast, systematische
                    Nachkalkulation, ein diversifizierter Finanzierungsmix und ein jährlich
                    berechneter Unternehmenswert. Der Inhaber steuert nach Instrumenten – bei jeder
                    Wetterlage.
                  </p>
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 pt-2">
                {CASE_STATS.map((s) => (
                  <div key={s.label} className="bg-card border border-white/10 p-4">
                    <span className="text-[0.65rem] uppercase tracking-[0.15em] text-white/40">
                      {s.label}
                    </span>
                    <div className="mt-2 flex items-center gap-2 text-sm">
                      <span className="text-white/40 line-through">{s.before}</span>
                      <ArrowRight className="h-3.5 w-3.5 text-brand-orange" />
                      <span className="text-white font-semibold">{s.after}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Result */}
            <div className="bg-brand-orange p-8">
              <TrendingUp className="h-8 w-8 text-black" />
              <div className="mt-6">
                <span className="font-display text-5xl text-black leading-none">+60 %</span>
                <p className="mt-2 text-sm font-bold text-black uppercase tracking-wide">
                  Unternehmenswert in 3 Jahren
                </p>
              </div>
              <p className="mt-4 text-sm text-black/75 leading-relaxed">
                Der Multiplikator stieg von 3,5x auf 5,8x — ohne einen Euro zusätzlichen Umsatz.
                Struktur wurde zu Wert.
              </p>
              <div className="mt-6 flex items-start gap-2 border-t border-black/20 pt-4">
                <Quote className="h-4 w-4 text-black/60 shrink-0 mt-0.5" />
                <p className="text-xs italic text-black/70 leading-relaxed">
                  „Zum ersten Mal habe ich das Gefühl, mein Unternehmen zu steuern – und nicht
                  umgekehrt."
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7 FIELDS */}
      <section className="relative max-w-7xl mx-auto px-6 sm:px-10 py-24">
        <span className="text-xs tracking-[0.2em] uppercase text-brand-orange">
          Die 7 Handlungsfelder des Tragfähigkeits-Checks
        </span>
        <p className="mt-3 text-sm text-white/50 max-w-xl">
          Jedes Feld hat sein eigenes Playbook – klicken Sie hinein und sehen Sie, wie
          Spitzensteuerung konkret aussieht.
        </p>
        <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {FIELDS_PREVIEW.map((f, i) => (
            <Link
              key={f}
              to={`/handlungsfeld/${i + 1}`}
              data-testid={`landing-hf-link-${i + 1}`}
              className="group bg-black p-6 flex flex-col justify-between min-h-[140px] hover:bg-brand-orange/5 transition-colors"
            >
              <span className="font-display text-brand-orange text-lg">
                {String(i + 1).padStart(2, "0")}
              </span>
              <span className="mt-4 text-sm font-semibold text-white/90 flex items-center justify-between gap-2">
                {f}
                <ArrowUpRight className="h-4 w-4 text-white/30 group-hover:text-brand-orange group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all shrink-0" />
              </span>
            </Link>
          ))}
          <button
            data-testid="landing-start-btn-secondary"
            onClick={onStart}
            className="bg-brand-orange p-6 flex flex-col justify-center min-h-[140px] text-left hover:bg-white transition-colors group"
          >
            <Rocket className="h-6 w-6 text-black" />
            <span className="mt-3 text-sm font-bold text-black uppercase tracking-wide flex items-center gap-1">
              Jetzt starten
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </span>
          </button>
        </div>
      </section>
    </div>
  );
};

export default Landing;
